import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { HiOutlineX } from "react-icons/hi";
import Button from "../../../../components/shared/Button/Button";
import Divider from "../../../../components/shared/Divider/Divider";
import Input from "../../../../components/shared/Input/Input";
import Select from "../../../../components/shared/Select/Select";
import SelectMulti from "../../../../components/shared/Select/SelectMulti";
import { useAdminSearchStore } from "../../store/adminSearchStore";

const formSchema = z.object({
  videoCreatedFrom: z.string().optional(),
  videoCreatedTo: z.string().optional(),
});

// Values from API response: videoSource (Sportsbox-iOS, Sportsbox-Android, External, Web)
const videoSourceOptions = [
  { text: "Sportsbox-iOS", value: "Sportsbox-iOS" },
  { text: "Sportsbox-Android", value: "Sportsbox-Android" },
  { text: "External", value: "External" },
  { text: "Web", value: "Web" },
];

// Values from API response: videoType (mov, MOV, MP4, video/quicktime, video/mp4, video/*)
const videoTypeOptions = [
  { text: "mov", value: "mov" },
  { text: "mp4", value: "mp4" },
  { text: "video/quicktime", value: "video/quicktime" },
];

// Values from API response: metaData.cameraAngle (FaceOn; DownTheLine is supported)
const cameraAngleOptions = [
  { text: "Face On", value: "FON" },
  { text: "Down the Line", value: "DTL" },
];

// Unique clubs: number-prefixed shown lowercased, others uppercased (driversClub + ironsClub + API variants 6, 7)
const golfClubOptions = [
  { text: "D", value: "D" },
  { text: "2w", value: "2w" },
  { text: "2h", value: "2h" },
  { text: "3w", value: "3w" },
  { text: "3h", value: "3h" },
  { text: "3i", value: "3i" },
  { text: "4h", value: "4h" },
  { text: "4i", value: "4i" },
  { text: "5w", value: "5w" },
  { text: "5i", value: "5i" },
  { text: "5h", value: "5h" },
  { text: "6", value: "6" },
  { text: "6h", value: "6h" },
  { text: "6i", value: "6i" },
  { text: "7", value: "7" },
  { text: "7w", value: "7w" },
  { text: "7i", value: "7i" },
  { text: "8i", value: "8i" },
  { text: "9w", value: "9w" },
  { text: "9i", value: "9i" },
  { text: "PW", value: "PW" },
  { text: "GW", value: "GW" },
  { text: "SW", value: "SW" },
  { text: "LW", value: "LW" },
];

export function VideoFiltersPanel({ isOpen, onClose, onSubmit }) {
  const { videosState, setVideosFilters } = useAdminSearchStore();
  const { filters } = videosState;

  const [draftFilters, setDraftFilters] = useState({
    userInput: "",
    videoSource: "",
    videoType: "",
    cameraAngle: "",
    golfClub: [],
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      videoCreatedFrom: "",
      videoCreatedTo: "",
    },
    mode: "onChange",
  });

  const watchedDates = watch(["videoCreatedFrom", "videoCreatedTo"]);

  useEffect(() => {
    if (isOpen) {
      setDraftFilters({
        userInput: filters.userInput || "",
        videoSource: filters.videoSource || "",
        videoType: filters.videoType || "",
        cameraAngle: filters.cameraAngle || "",
        golfClub: Array.isArray(filters.golfClub) ? [...filters.golfClub] : [],
      });
      setValue(
        "videoCreatedFrom",
        filters.videoCreated?.from
          ? moment(filters.videoCreated.from).format("YYYY-MM-DD")
          : ""
      );
      setValue(
        "videoCreatedTo",
        filters.videoCreated?.to
          ? moment(filters.videoCreated.to).format("YYYY-MM-DD")
          : ""
      );
    }
  }, [filters, isOpen, setValue]);

  const hasActiveFilters = useMemo(() => {
    if ((draftFilters.userInput || "").trim()) return true;
    if ((draftFilters.videoSource || "").trim()) return true;
    if ((draftFilters.videoType || "").trim()) return true;
    if ((draftFilters.cameraAngle || "").trim()) return true;
    if (draftFilters.golfClub.length > 0) return true;
    if (watchedDates.some((d) => (d || "").trim())) return true;
    return false;
  }, [draftFilters, watchedDates]);

  const handleClear = useCallback(() => {
    setDraftFilters({
      userInput: "",
      videoSource: "",
      videoType: "",
      cameraAngle: "",
      golfClub: [],
    });
    reset({
      videoCreatedFrom: "",
      videoCreatedTo: "",
    });
    setVideosFilters({
      userInput: "",
      videoSource: "",
      videoType: "",
      videoCreated: { from: null, to: null },
      golfClub: [],
      cameraAngle: "",
    });
    onSubmit?.();
  }, [reset, setVideosFilters, onSubmit]);

  const handleFormSubmit = useCallback(
    (data) => {
      setVideosFilters({
        userInput: draftFilters.userInput?.trim() || "",
        videoSource: draftFilters.videoSource || "",
        videoType: draftFilters.videoType || "",
        cameraAngle: draftFilters.cameraAngle || "",
        golfClub: draftFilters.golfClub || [],
        videoCreated: {
          from: data.videoCreatedFrom
            ? moment(data.videoCreatedFrom).startOf("day").valueOf()
            : null,
          to: data.videoCreatedTo
            ? moment(data.videoCreatedTo).endOf("day").valueOf()
            : null,
        },
      });
      onSubmit?.();
    },
    [draftFilters, setVideosFilters, onSubmit]
  );

  if (!isOpen) return null;

  return (
    <div
      aria-label="Video Filters"
      aria-modal="true"
      className="font-manrope fixed right-0 top-0 z-[2000] flex h-screen w-full max-w-96 flex-col border-l border-gray200 bg-white p-6"
      role="dialog"
    >
      <div className="relative flex flex-col gap-1">
        <div className="text-2xl font-semibold">Filters</div>
        <div className="text-base text-gray600">Filter videos by criteria</div>
        <button
          aria-label="Close"
          className="absolute right-0 top-0 w-fit rounded-sm p-2"
          onClick={onClose}
        >
          <HiOutlineX className="size-6 text-gray500" />
        </button>
      </div>

      <div className="mt-6 flex flex-1 flex-col gap-6 overflow-y-auto scrollbar-hide">
        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Parent User ID or email</label>
          <input
            className="h-12 w-full rounded-lg border border-gray300 px-4 text-base outline-none transition-all duration-300 placeholder:text-gray500 focus:border-primary300 focus:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#F4EBFF]"
            placeholder="Parent user ID or email"
            type="text"
            value={draftFilters.userInput}
            onChange={(e) =>
              setDraftFilters((prev) => ({
                ...prev,
                userInput: e.target.value,
              }))
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Video Source</label>
          <Select
            options={videoSourceOptions}
            value={draftFilters.videoSource || null}
            placeholder="Any source"
            withCheckbox
            buttonProps={{ size: "md" }}
            onChange={(val) =>
              setDraftFilters((prev) => ({
                ...prev,
                videoSource: val === prev.videoSource ? "" : val,
              }))
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Video Type</label>
          <Select
            options={videoTypeOptions}
            value={draftFilters.videoType || null}
            placeholder="Any type"
            withCheckbox
            buttonProps={{ size: "md" }}
            onChange={(val) =>
              setDraftFilters((prev) => ({
                ...prev,
                videoType: val === prev.videoType ? "" : val,
              }))
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Camera Angle</label>
          <Select
            options={cameraAngleOptions}
            value={draftFilters.cameraAngle || null}
            placeholder="Any angle"
            withCheckbox
            buttonProps={{ size: "md" }}
            onChange={(val) =>
              setDraftFilters((prev) => ({
                ...prev,
                cameraAngle: val === prev.cameraAngle ? "" : val,
              }))
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Golf Club</label>
          <SelectMulti
            header="Golf Club"
            options={golfClubOptions}
            values={draftFilters.golfClub}
            onChange={(val) => {
              const current = draftFilters.golfClub;
              const next = current.includes(val)
                ? current.filter((v) => v !== val)
                : [...current, val];
              setDraftFilters((prev) => ({ ...prev, golfClub: next }));
            }}
            onClear={() =>
              setDraftFilters((prev) => ({ ...prev, golfClub: [] }))
            }
            placeholder="Select clubs..."
            buttonProps={{
              rounded: "full",
              size: "md",
              className: "flex w-full flex-row items-center gap-2",
            }}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className="text-base text-gray700">Video Created From</label>
            <Input
              control={control}
              name="videoCreatedFrom"
              type="date"
              size="md"
              fullWidth
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-base text-gray700">Video Created To</label>
            <Input
              control={control}
              name="videoCreatedTo"
              type="date"
              size="md"
              fullWidth
            />
          </div>
        </div>
      </div>

      <div className="mt-auto">
        <Divider type="horizontal" color="gray200" />

        <div className="flex items-center justify-between pt-4">
          <button
            className={`text-lg font-semibold ${
              !hasActiveFilters ? "text-gray300" : "text-primary700"
            }`}
            onClick={handleClear}
          >
            Clear all
          </button>

          <div className="flex gap-2">
            <Button color="gray100" onClick={onClose} size="md">
              Cancel
            </Button>

            <Button
              onClick={handleSubmit(handleFormSubmit)}
              size="md"
              isDisabled={!isDirty && !hasActiveFilters}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
